function clone(value) {
  return JSON.parse(JSON.stringify(value || []));
}

function createSkuKeySet(data) {
  return new Set((data || []).map((item) => String(item?.skuId)));
}

function getDefaultFilterForm() {
  return {
    quoteTime: "",
    keyword: "",
    region: "",
    quoteType: "",
  };
}

function normalizeText(value) {
  return String(value || "").trim().toLowerCase();
}

function normalizeMonthValue(value) {
  const text = String(value || "").trim();
  const match = text.match(/^(\d{4})-(\d{1,2})/);
  if (!match) {
    return "";
  }
  return `${match[1]}-${match[2].padStart(2, "0")}`;
}

function splitShipAreas(shipArea) {
  if (Array.isArray(shipArea)) {
    return shipArea.map((item) => String(item || "").trim()).filter(Boolean);
  }
  return String(shipArea || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

function getRegionLabel(quote) {
  const areas = splitShipAreas(quote?.shipArea);
  return areas.length ? areas[areas.length - 1] : "";
}

function getQuoteTypeValue(quote) {
  return quote?.isNewest === "0" ? "auto" : "manual";
}

function matchesKeyword(row, quote, keyword) {
  if (!keyword) {
    return true;
  }
  const searchText = [
    row?.brandName,
    row?.productName,
    row?.barCode,
    row?.articleNo,
    quote?.sellerEnterpriseName,
    quote?.sellerName,
  ]
    .map((item) => normalizeText(item))
    .join("|");
  return searchText.includes(keyword);
}

function matchesQuote(quote, row, filters) {
  const keyword = normalizeText(filters.keyword);
  if (!matchesKeyword(row, quote, keyword)) {
    return false;
  }

  if (filters.quoteTime) {
    const quoteMonth = normalizeMonthValue(quote?.quoteTime);
    const selectedMonth = normalizeMonthValue(filters.quoteTime);
    if (!quoteMonth || quoteMonth !== selectedMonth) {
      return false;
    }
  }

  if (filters.region && getRegionLabel(quote) !== filters.region) {
    return false;
  }

  if (filters.quoteType && getQuoteTypeValue(quote) !== filters.quoteType) {
    return false;
  }

  return true;
}

function buildRowPriceStats(row, companyList) {
  const prices = companyList
    .map((companyItem) => {
      const quote = companyItem[row.skuId];
      return Number(quote?.salesPrice);
    })
    .filter((price) => Number.isFinite(price) && price > 0);

  row.minPurchasePrice = prices.length ? Math.min(...prices).toFixed(2) : "";
  row.maxPurchasePrice = prices.length ? Math.max(...prices).toFixed(2) : "";
}

function buildOfferFilterState(forSku, offerProfitCompute) {
  const data = clone(forSku);
  const company = [];
  const skuKeySet = createSkuKeySet(data);

  data.forEach((item) => {
    const enterpriseList = Array.isArray(item.enterpriseVOList)
      ? item.enterpriseVOList
      : [];

    enterpriseList.forEach((quote) => {
      let quoteInfo = {
        qty: quote.qty,
        purchasePrice: quote.purchasePrice,
        profitSite: quote.profitSite,
        isNewest: quote.isNewest,
        guideSite: quote.guideSite,
        channel: quote.channel,
        quoteTime: quote.quoteTime,
        guidePrice: quote.guidePrice,
        salesDis: quote.salesDis,
        itemList: quote.itemList,
        sellerName: quote.sellerName,
        salesPrice: quote.salesPrice,
        salesPlatform: quote.salesPlatform,
        purchaseDis: quote.purchaseDis,
        brandId: item.brandId,
        skuId: item.skuId,
        // 同一商品行可能包含多家企业报价，item.itemId 不能代表当前企业的报价明细。
        // 保留当前 quote 自身的明细 ID，后续手动选择或直接添加购物车时与 quoteId 配套提交。
        itemId: quote.itemId,
        // 优先保留报价所属的询价单 ID；缺省时补充商品行所属的询价单 ID。
        inquiryId: quote.inquiryId || item.inquiryId,
        quoteId: quote.quoteId || item.quoteId,
      };

      quoteInfo = offerProfitCompute(quoteInfo);
      quoteInfo.shipArea = quote.shipArea;
      quoteInfo.sellerEnterpriseName = quote.sellerEnterpriseName;
      quoteInfo.sellerEnterpriseId = quote.sellerEnterpriseId;
      quoteInfo.sellerId = quote.sellerId;

      const existedIndex = company.findIndex(
        (companyItem) => companyItem.sellerEnterpriseId === quote.sellerEnterpriseId
      );

      if (existedIndex > -1) {
        company[existedIndex][item.skuId] = quoteInfo;
      } else {
        company.push({
          sellerEnterpriseId: quote.sellerEnterpriseId,
          sellerEnterpriseName: quote.sellerEnterpriseName,
          sellerId: quote.sellerId,
          sellerName: quote.sellerName,
          purchasePrice: quote.purchasePrice,
          isAgent: quote.isAgent,
          agentList: quote.agentList,
          proprietaryFlag: quote.proprietaryFlag,
          authFlag: quote.authFlag,
          [item.skuId]: quoteInfo,
        });
      }
    });
  });

  const regionOptions = Array.from(
    new Set(
      company
        .flatMap((companyItem) =>
          Object.keys(companyItem)
            .filter((key) => skuKeySet.has(String(key)))
            .map((key) => getRegionLabel(companyItem[key]))
        )
        .filter(Boolean)
    )
  );

  return {
    data,
    company,
    regionOptions,
    skuKeySet: Array.from(skuKeySet),
  };
}

function applyOfferFilters(state, filters) {
  const normalizedFilters = {
    ...getDefaultFilterForm(),
    ...(filters || {}),
  };
  const data = clone(state?.data);
  const rawCompany = clone(state?.company);
  const skuKeySet = new Set(state?.skuKeySet || createSkuKeySet(data));
  const filteredCompany = [];
  const quoteCounter = new Map();

  rawCompany.forEach((companyItem) => {
    const nextCompany = {
      sellerEnterpriseId: companyItem.sellerEnterpriseId,
      sellerEnterpriseName: companyItem.sellerEnterpriseName,
      sellerId: companyItem.sellerId,
      sellerName: companyItem.sellerName,
      purchasePrice: companyItem.purchasePrice,
      isAgent: companyItem.isAgent,
      agentList: companyItem.agentList,
      proprietaryFlag: companyItem.proprietaryFlag,
      authFlag: companyItem.authFlag,
    };
    let hasMatchedQuote = false;

    data.forEach((row) => {
      const quote = companyItem[row.skuId];
      if (quote && matchesQuote(quote, row, normalizedFilters)) {
        nextCompany[row.skuId] = quote;
        hasMatchedQuote = true;
        quoteCounter.set(row.skuId, (quoteCounter.get(row.skuId) || 0) + 1);
      }
    });

    if (hasMatchedQuote) {
      filteredCompany.push(nextCompany);
    }
  });

  const filteredData = data
    .filter((row) => quoteCounter.has(row.skuId))
    .map((row) => {
      buildRowPriceStats(row, filteredCompany);
      return row;
    });

  return {
    data: filteredData,
    company: filteredCompany,
    quoteCount: filteredCompany.reduce((count, companyItem) => {
      return (
        count +
        Object.keys(companyItem).filter((key) => skuKeySet.has(String(key))).length
      );
    }, 0),
  };
}

module.exports = {
  applyOfferFilters,
  buildOfferFilterState,
  getDefaultFilterForm,
  normalizeMonthValue,
};
